import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Monogram } from "../components/Monogram";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/format";

export default function Auth({ mode = "login" }) {
  const navigate = useNavigate();
  const { user, loading, login, register } = useAuth();
  const isSignup = mode === "signup";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate(user.role === "admin" ? "/reef" : "/portal", { replace: true });
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = isSignup ? await register({ email, password, name, phone }) : await login(email, password);
      toast.success(isSignup ? "Welcome to the studio." : "You're in.");
      navigate(u.role === "admin" ? "/reef" : "/portal", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-paper">
      {/* Left — brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-ink text-paper p-12">
        <Link to="/" className="flex items-center gap-3" data-testid="auth-home-link">
          <Monogram size={44} className="!border-brass !text-brass" />
          <span className="font-serif text-xl text-paper" style={{ fontWeight: 600 }}>
            Working Actor
          </span>
        </Link>
        <div>
          <h2 className="font-serif text-5xl leading-tight text-paper">
            The room is<br />
            <span className="italic text-brass">small on purpose.</span>
          </h2>
          <p className="font-sans text-sm text-paper/70 mt-6 max-w-sm leading-relaxed">
            Your sides, your classes, and your billing — in one calm place. Sign in to get off-book for the next
            session.
          </p>
        </div>
        <p className="overline !text-brass/70">The Acting Studio of Eliza Roberts</p>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <Monogram size={40} />
            <span className="font-serif text-lg" style={{ fontWeight: 600 }}>
              Working Actor
            </span>
          </div>

          <h1 className="font-serif text-4xl text-ink mb-2">{isSignup ? "Request a spot" : "Welcome back"}</h1>
          <p className="font-sans text-sm text-stone mb-8">
            {isSignup ? "Create your student account to join a class." : "Sign in to your studio portal."}
          </p>

          <button onClick={handleGoogle} className="btn-outline w-full mb-5" data-testid="google-auth-btn">
            <span className="font-serif text-lg leading-none">G</span> Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="hair-rule flex-1" />
            <span className="font-sans text-xs text-stone uppercase tracking-wider">or</span>
            <div className="hair-rule flex-1" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignup && (
              <div>
                <label className="field-label">Full name</label>
                <input
                  className="field-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Your name"
                  data-testid="auth-name-input"
                />
              </div>
            )}
            <div>
              <label className="field-label">Email</label>
              <input
                type="email"
                className="field-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@email.com"
                data-testid="auth-email-input"
              />
            </div>
            {isSignup && (
              <div>
                <label className="field-label">Phone (optional)</label>
                <input
                  className="field-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(323) 555-0100"
                  data-testid="auth-phone-input"
                />
              </div>
            )}
            <div>
              <label className="field-label">Password</label>
              <input
                type="password"
                className="field-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={isSignup ? 6 : undefined}
                placeholder="••••••••"
                data-testid="auth-password-input"
              />
            </div>

            {error && (
              <p className="font-sans text-sm text-garnet" data-testid="auth-error">
                {error}
              </p>
            )}

            <button type="submit" disabled={busy} className="btn-brass w-full" data-testid="auth-submit-btn">
              {busy ? "One moment…" : isSignup ? "Create account" : "Sign in"}
            </button>
          </form>

          <p className="font-sans text-sm text-stone mt-6 text-center">
            {isSignup ? (
              <>
                Already have an account?{" "}
                <Link to="/login" className="text-brass hover:underline" data-testid="switch-to-login">
                  Sign in
                </Link>
              </>
            ) : (
              <>
                New to the studio?{" "}
                <Link to="/signup" className="text-brass hover:underline" data-testid="switch-to-signup">
                  Request a spot
                </Link>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
